import moment from 'moment';
import { mapState } from 'vuex';
import { VENUE_STORAGE, VENUE_ASSETS_DEFAULT } from '@/constants';
import { isValidTime, isValidDate, isValidTimeAllowNull } from '@/services/helpers';

export default {
	created() {
		this.$store.dispatch('config/GET_CMS_CLIENT_CONFIG', null, { root: true });
	},
	data: () => ({
		basicRules: [
			v => !!v || 'This field is required'
		],
		arrayRules: [
			v => v.length > 0 || 'This field is required'
		],
		passwordRules: [
			v => !!v || 'Password is required',
			v => (v && (v.length >= 8 && v.length <= 16)) || 'Password must be at least 8 to 16 characters',
			v => /[`!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?~]/.test(v) || 'Password must have at least 1 special character',
			v => /\d/.test(v) || 'Password must have at least 1 number'
		],
		emailRules: [
			v => !!v || 'E-mail is required',
			v => /\S+@\S+\.\S+/.test(v) || 'E-mail must be valid'
		],
		timeRules: [
			v => !!v || 'This field is required',
			v => isValidTime(v) || 'Invalid time format'
		],
    timeAllowNullRules: [
      v => isValidTimeAllowNull(v) || 'Invalid time format'
    ],
		dateRules: [
			v => !!v || 'This field is required',
			v => isValidDate(v) || 'Invalid date format'
		],
		numberRules: [
			v => (v && /^[0-9.:]+$/.test(v)) || 'Field only accepts numbers, colons and period'
		],
		opacityRules: [
			v => (v && /^[0-9.:]+$/.test(v)) || 'Field only accepts numbers, colons and period',
			v => (Number(v) > 0 && Number(v) <= 1)  || 'Number should be greater than 0 and less than equal to 1',
		],
		snackbar: false,
		snackbarMessage: null,
		snackbarColor: null,
		noDataText: 'Loading...'
	}),
	methods: {
		resetNoDataText() {
			this.noDataText = 'No data available';
		},
		calculateAspectRatioFit(srcWidth, srcHeight, maxWidth = 120, maxHeight = 120) {
			var ratio = 1, width = maxWidth, height = maxHeight;

			if (srcWidth > 0 && srcHeight > 0) {
				ratio = Math.min(maxWidth / srcWidth, maxHeight / srcHeight);
				width = srcWidth * ratio;
				height = srcHeight * ratio;
			}

			return {
				width: width,
				height: height
			}
		},
		removeColons(val) {
			return +val.replace(/:/g, '')
		},
		convertSecondsToMinutes(seconds) {
			// Calculate minutes and remaining seconds
			const minutes = Math.floor(seconds / 60);
			const remainingSeconds = seconds % 60;

			// Format with leading zeros if necessary
			const formattedMinutes = String(minutes).padStart(2, '0');
			const formattedSeconds = String(remainingSeconds).padStart(2, '0');

			// Return the formatted time string
			return `${formattedMinutes}:${formattedSeconds}`;
		},
		chunk(str, n) {
			let ret = []
			let len
			for (let i = 0, len = str.length; i < len; i += n) {
				ret.push(str.substr(i, n))
			}
			return ret
		},
		makeid() {
			var text = "";
			var possible = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";

			for (var i = 0; i < 5; i++)
				text += possible.charAt(Math.floor(Math.random() * possible.length));

			return text;
		},
		militaryToNormal(time) {
			time = time.split(':') // convert to array

			// fetch
			var hours = Number(time[0])
			var minutes = Number(time[1])
			var seconds = Number(time[2])

			// calculate
			var timeValue;

			if (hours > 0 && hours <= 12) {
				timeValue = "" + hours
			} else if (hours > 12) {
				timeValue = "" + (hours - 12)
			}
			else if (hours == 0) {
				timeValue = "12"
			}

			timeValue += (minutes < 10) ? ":0" + minutes : ":" + minutes;  // get minutes
			//timeValue += (seconds < 10) ? ":0" + seconds : ":" + seconds;  // get seconds
			timeValue += (hours >= 12) ? " PM" : " AM";  // get AM/PM
			return timeValue
		},
		searchQuery() {
			const storage = localStorage.getItem('cms_search');

			return {
				get(key) {
					if (storage) {
						const s = JSON.parse(storage);
						if (s[key]) {
							return s[key];
						} else {
							return null;
						}
					} else {
						return null;
					}
				},

				save(key, value) {
					let s = storage ? JSON.parse(storage) : {};
					s[key] = value;
					localStorage.setItem('cms_search', JSON.stringify(s));
					return true;
				},

				remove(key) {
					const s = JSON.parse(storage);
					if (!s) return;
					if (s[key]) {
						delete s[key];
						localStorage.setItem('cms_search', JSON.stringify(s));
						return true;
					} else {
						return null;
					}
				}
			}
		},
		addDefaultLogoToVenue(venue) {
			const propName = 'venueAsset';

			if (!venue.hasOwnProperty(propName)) {
				venue[propName] = VENUE_ASSETS_DEFAULT;
			}

			return venue;
		}
	},
	computed: {
		...mapState('users', {
			userVenues: 'userVenues',
			authenticatedUser: 'user'
		}),
		selectedVenue() {
			const selected = localStorage.getItem(VENUE_STORAGE);

			if (selected) {
				try {
					const venue = this.userVenues.find((i) => i._id === JSON.parse(selected)._id);
					return venue;
				} catch (e) {
					return null;
				}
			}

			return null;
		},
		cmsLogo() {
			const selected = this.selectedVenue;

			return selected != null && selected.venueAsset != null && selected.venueAsset.cms_logo != null ? selected.venueAsset.cms_logo : VENUE_ASSETS_DEFAULT.cms_logo;
		},
		loginLogo() {
			const config = this.$store.state.config.cms_client_config;

			if (config != null) {
				if (config.pageAsset != null) {
					if (config.pageAsset.login_logo != null) {
						if (config.pageAsset.login_logo != '') {
							return config.pageAsset.login_logo;
						}
					}
				}
			}

			return VENUE_ASSETS_DEFAULT.login_logo;
		}
	},
	filters: {
		formatSizeUnits(val) {
			let bytes = val
			if (bytes >= 1073741824) {
				bytes = (bytes / 1073741824).toFixed(2) + ' GB'
			} else if (bytes >= 1048576) {
				bytes = (bytes / 1048576).toFixed(2) + ' MB'
			} else if (bytes >= 1024) {
				bytes = (bytes / 1024).toFixed(2) + ' KB'
			} else if (bytes > 1) {
				bytes = bytes + ' bytes'
			} else if (bytes == 1) {
				bytes = bytes + ' byte'
			} else {
				bytes = '0 byte'
			}
			return bytes
		},
		formatBytes(bytes, decimals) {
			if (!bytes || typeof bytes === 'undefined') return ''
			if (bytes == 0) return '0 Bytes'
			const k = 1024
			const dm = decimals || 2
			const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB']
			const i = Math.floor(Math.log(bytes) / Math.log(k))
			return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i]
		},
		fileType(val) {
			let type
			if (val.includes('image')) {
				type = 'Image'
			} else if (val.includes('video')) {
				type = 'Video'
			} else if (val.includes('zip') || val.includes('rar')) {
				type = 'Compressed'
			} else {
				type = val
			}
			return type
		},
		duration(time) {
			let finalTime
			if (typeof time === 'number') {
				time = Math.trunc(time)
				finalTime = ~~(time / 60) + ":" + (time % 60 < 10 ? "0" : "") + time % 60
			} else {
				finalTime = ''
			}
			return finalTime
		},
		fullDate(val) {
			return moment(val).format('MMMM D, YYYY h:mm A')
		},
		addColons(val) {
			val = String('000000' + val).slice(-6)
			let ret = []
			let len
			for (let i = 0, len = val.length; i < len; i += 2) {
				ret.push(val.substr(i, 2))
			}
			return ret.join(':')
		},
		truncate(val, len) {
			if (val.length > len) {
				return val.substring(0, len) + '...';
			}
			else {
				return val;
			}
		}
	}
}



// WEBPACK FOOTER //
// ./src/mixins/index.js